import { clearStoredReferral, getStoredReferral } from '../utils/referralStorage';

// Убеждаемся, что SERVER_URL всегда содержит префикс /api
const getServerUrl = () => {
  const envUrl = process.env.REACT_APP_SERVER_URL;
  if (envUrl) {
    // Если URL из переменной окружения заканчивается на /api, оставляем как есть
    // Иначе добавляем /api
    return envUrl.endsWith('/api') ? envUrl : `${envUrl}/api`;
  }
  return '/api';
};

const SERVER_URL = getServerUrl();

export { SERVER_URL };

/** Сообщение из JSON ответа API (Nest + HttpExceptionFilter: message или _error.text). */
function extractApiErrorMessage(errorData) {
  if (!errorData || typeof errorData !== 'object') return null;
  if (typeof errorData.message === 'string') return errorData.message;
  if (Array.isArray(errorData.message)) return errorData.message.join('\n');
  const text = errorData._error && typeof errorData._error.text === 'string' ? errorData._error.text : null;
  return text;
}

const SERVER_UNAVAILABLE_MESSAGE = 'Сервер временно недоступен. Пожалуйста, попробуйте позже.';
const SPREAD_HISTORY_INSTRUMENT_NOT_FOUND = 'Инструмент не найден в справочнике BCS';

function isSpreadHistoryUnavailable(status, errorData) {
  if (status === 404) {
    return true;
  }

  const text = extractApiErrorMessage(errorData) || '';
  const stack = typeof errorData?._error?.details?.stack === 'string'
    ? errorData._error.details.stack
    : '';

  return text.includes(SPREAD_HISTORY_INSTRUMENT_NOT_FOUND)
    || stack.includes(SPREAD_HISTORY_INSTRUMENT_NOT_FOUND);
}

class AuthService {
  constructor() {
    this.tokenKey = 'auth_token';
    this.refreshTokenKey = 'refresh_token';
  }

  // Сохранение токенов в localStorage
  setTokens(accessToken, refreshToken) {
    localStorage.setItem(this.tokenKey, accessToken);
    localStorage.setItem(this.refreshTokenKey, refreshToken);
  }

  // Получение access token
  getAccessToken() {
    return localStorage.getItem(this.tokenKey);
  }

  // Получение refresh token
  getRefreshToken() {
    return localStorage.getItem(this.refreshTokenKey);
  }

  // Удаление токенов
  clearTokens() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
  }

  // Проверка авторизации
  isAuthenticated() {
    return !!this.getAccessToken();
  }

  // Регистрация
  async register(email, password) {
    try {
      const ref = getStoredReferral();
      const body = { email, password };
      if (ref) {
        body.ref = ref;
      }

      const response = await fetch(`${SERVER_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        // Проверяем, является ли ответ JSON
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Ошибка регистрации');
        } else {
          // Если это не JSON (например, HTML страница ошибки), показываем общую ошибку
          throw new Error(`Ошибка сервера: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      this.setTokens(data.accessToken, data.refreshToken);
      clearStoredReferral();
      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // Вход
  async login(email, password) {
    try {
      const response = await fetch(`${SERVER_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        if (response.status >= 500) {
          throw new Error(SERVER_UNAVAILABLE_MESSAGE);
        }

        // Проверяем, является ли ответ JSON
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Неверный email или пароль');
        }

        throw new Error(SERVER_UNAVAILABLE_MESSAGE);
      }

      const data = await response.json();
      this.setTokens(data.accessToken, data.refreshToken);
      return data;
    } catch (error) {
      console.error('Login error:', error);
      if (error instanceof TypeError || error.message === 'Failed to fetch') {
        throw new Error(SERVER_UNAVAILABLE_MESSAGE);
      }
      throw error;
    }
  }

  // Обновление токена
  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${SERVER_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${refreshToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      this.setTokens(data.accessToken, data.refreshToken);
      return data;
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearTokens();
      throw error;
    }
  }

  // Получение профиля пользователя
  async getProfile() {
    try {
      const accessToken = this.getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${SERVER_URL}/auth/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Попытка обновить токен
          await this.refreshToken();
          return this.getProfile(); // Рекурсивный вызов с новым токеном
        }
        throw new Error('Failed to get profile');
      }

      return await response.json();
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }

  // Выход
  logout() {
    this.clearTokens();
    // Можно добавить вызов API для logout на сервере
  }

  async forgotPassword(email) {
    try {
      const response = await fetch(`${SERVER_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        if (response.status >= 500) {
          throw new Error(SERVER_UNAVAILABLE_MESSAGE);
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(extractApiErrorMessage(errorData) || 'Не удалось отправить запрос');
        }

        throw new Error(SERVER_UNAVAILABLE_MESSAGE);
      }

      return await response.json();
    } catch (error) {
      console.error('Forgot password error:', error);
      if (error instanceof TypeError || error.message === 'Failed to fetch') {
        throw new Error(SERVER_UNAVAILABLE_MESSAGE);
      }
      throw error;
    }
  }

  async resetPassword(token, password) {
    try {
      const response = await fetch(`${SERVER_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      if (!response.ok) {
        if (response.status >= 500) {
          throw new Error(SERVER_UNAVAILABLE_MESSAGE);
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(extractApiErrorMessage(errorData) || 'Не удалось сменить пароль');
        }

        throw new Error(SERVER_UNAVAILABLE_MESSAGE);
      }

      return await response.json();
    } catch (error) {
      console.error('Reset password error:', error);
      if (error instanceof TypeError || error.message === 'Failed to fetch') {
        throw new Error(SERVER_UNAVAILABLE_MESSAGE);
      }
      throw error;
    }
  }

  // Создание заголовков с токеном для API запросов
  getAuthHeaders() {
    const token = this.getAccessToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async fetchSpreadHistory(stock, futures) {
    const url = `${SERVER_URL}/v1/spread-history`;
    const requestOptions = {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ stock, futures }),
    };

    const request = () => fetch(url, requestOptions);

    try {
      let response = await request();

      if (response.status === 401) {
        await this.refreshToken();
        requestOptions.headers = this.getAuthHeaders();
        response = await request();
      }

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        const errorData = contentType && contentType.includes('application/json')
          ? await response.json()
          : null;

        if (isSpreadHistoryUnavailable(response.status, errorData)) {
          return { unavailable: true };
        }

        const message = extractApiErrorMessage(errorData) || `Request failed: ${response.status}`;
        return { error: message };
      }

      return { data: await response.json() };
    } catch (error) {
      console.error('Spread history request error:', error);
      if (error instanceof TypeError || error.message === 'Failed to fetch') {
        return { error: SERVER_UNAVAILABLE_MESSAGE };
      }

      return { error: error.message };
    }
  }

  // Обертка для API запросов с автоматическим обновлением токена
  async authenticatedRequest(url, options = {}) {
    try {
      const headers = {
        ...this.getAuthHeaders(),
        ...options.headers,
      };

      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status === 401) {
        // Попытка обновить токен
        await this.refreshToken();
        
        // Повторный запрос с новым токеном
        const newHeaders = {
          ...this.getAuthHeaders(),
          ...options.headers,
        };

        const retryResponse = await fetch(url, {
          ...options,
          headers: newHeaders,
        });

        if (!retryResponse.ok) {
          // Проверяем, является ли ответ JSON
          const contentType = retryResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await retryResponse.json();
            throw new Error(extractApiErrorMessage(errorData) || `Request failed: ${retryResponse.status}`);
          } else {
            throw new Error(`Ошибка сервера: ${retryResponse.status} ${retryResponse.statusText}`);
          }
        }

        return retryResponse;
      }

      if (!response.ok) {
        // Проверяем, является ли ответ JSON
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(extractApiErrorMessage(errorData) || `Request failed: ${response.status}`);
        } else {
          throw new Error(`Ошибка сервера: ${response.status} ${response.statusText}`);
        }
      }

      return response;
    } catch (error) {
      console.error('Authenticated request error:', error);
      throw error;
    }
  }
}

export default new AuthService();
